import React, { useState, useRef, useEffect } from 'react';
import { useSwipeable } from 'react-swipeable';
import { AiFillHeart, AiOutlineHeart, AiOutlineComment } from 'react-icons/ai';
import { HiOutlineMenu } from 'react-icons/hi';
import { IoCloseOutline } from 'react-icons/io5';
import { BsSoundwave, BsVolumeMute } from 'react-icons/bs';

const TextDescription = ({ text }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const MAX_CHARS = 100;

  if (text.length <= MAX_CHARS) {
    return <p style={{ margin: 0, fontSize: '1rem', lineHeight: '1.4' }}>{text}</p>;
  }

  return (
    <p style={{ margin: 0, fontSize: '1rem', lineHeight: '1.4' }}>
      {isExpanded ? text : `${text.slice(0, MAX_CHARS)}...`}
      <span
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          fontWeight: 'bold',
          marginLeft: '4px',
          cursor: 'pointer',
          color: '#fff',
          textDecoration: 'underline'
        }}
      >
        {isExpanded ? 'less' : 'more'}
      </span>
    </p>
  );
};

const TikTok = () => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(window.innerWidth > 768);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 768);
  const [commentsPanelY, setCommentsPanelY] = useState(0);
  const [isDraggingComments, setIsDraggingComments] = useState(false);
  const videoRefs = [useRef(null), useRef(null), useRef(null)];
  const [showConsentDialog, setShowConsentDialog] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [videoPlaybackAllowed, setVideoPlaybackAllowed] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [likedVideos, setLikedVideos] = useState({});
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const videoContent = [
    {
      src: '/whats-on-demo.mp4',
      display: true,
      title: "What's On",
      description: "Discover new events in Amsterdam - an iOS app backed by a Rails API that I built which scrapes events from around the internet and allows easy sharing and discovery for users. It features an instagram-style recommendation algorithm, and embeddings based search to give advanced search results.",
      video_id: 1
    },
    {
      src: '/tiny-notes-demo.mp4',
      display: true,
      title: "Tiny Notes",
      description: "A side project I built which combined obsidian style bi-directionally linked notes, and anki style flash card software in an attempt to make a second brain which helped me to both connect the things I was learning, and remember better what I'd learnt.",
      video_id: 2
    },
    {
      src: '/recoverable-transfers-demo.mp4',
      display: true,
      title: "Recoverable Transfers",
      description: "A revenue experiment at WeTransfer that we designed, built, and launched in one week to ~1m users. The experiment allowed files to be recovered after the transfer was 'expired' for a fee or if the user was paying for the service. Users loved this experiment, it was projected to increase subsription revenue by 12%, and was quickly rolled out worldwide.",
      video_id: 3
    },
    {
      src: '/insta-true-chrome-extension.mp4',
      display: true,
      title: "InstaTrue?",
      description: "A chrome extension I vibecoded which allows you to fact check instagram posts, using Claude and web search on the backend.",
      video_id: 4
    },
    {
      src: '/whats-on-3.MP4',
      display: false,
      title: "Slides Website Builder [Coming Soon]",
      description: "Another hackathon project. At the time WeTransfer had a slide deck tool, a competitor to Google Slides. In the hackathon I added functionality to share the slide deck as a long scrolling single page website, with pre-made elements the user could add, such as a newsletter signup form. This work was an inspiration for a whole new team focussed on allowing users to customise their presence on wetransfer.com.",
      video_id: 5
    },
    {
      src: '/whats-on-3.MP4',
      display: false,
      title: "Strava Book Prints [Coming Soon]",
      description: "In 2022 I cycled from Amsterdam to Istanbul over three months. It was an awesome adventure which I recorded on Strava, and afterwards I wanted to make a memory book of the experience. After first starting on a physical book I decided I could probably make a tool to make the book for me faster than I could make the book itself. So, StravaBookPrints was born. It's a Strava integration which allows you to make a memory book based on your Strava activities, and then prints out and delivers the real book to you, something my dad and his friends have been enjoying.",
      video_id: 6
    }
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsSidebarExpanded(window.innerWidth > 768);
      setIsSmallScreen(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    videoRefs.forEach((ref, index) => {
      if (!videoPlaybackAllowed) {
        ref.current?.pause();
        return;
      }

      if (index === currentVideoIndex) {
        ref.current?.play();
      } else {
        ref.current?.pause();
      }
    });
  }, [currentVideoIndex, videoPlaybackAllowed]);

  useEffect(() => {
    if (audioEnabled) {
      videoRefs.forEach((ref, index) => {
        if (index === currentVideoIndex && ref.current) {
          ref.current.muted = false;
          ref.current.play();
        }
      });
    }
  }, [audioEnabled, currentVideoIndex]);

  useEffect(() => {
    // Load liked videos from localStorage on component mount
    const savedLikes = localStorage.getItem('likedVideos');
    if (savedLikes) {
      setLikedVideos(JSON.parse(savedLikes));
    }
  }, []);

  useEffect(() => {
    // Load comments for current video
    const fetchComments = async () => {
      try {
        const response = await fetch(`/comments?video_id=${encodeURIComponent(videoContent[currentVideoIndex].video_id)}`);
        if (response.ok) {
          const data = await response.json();
          setComments(prevComments => ({
            ...prevComments,
            [videoContent[currentVideoIndex].video_id]: data
          }));
        }
      } catch (error) {
        console.error('Error fetching comments:', error);
      }
    };

    if (showComments) {
      fetchComments();
    }
  }, [currentVideoIndex, showComments]);

  const handleLikeVideo = (videoId) => {
    const newLikedVideos = {
      ...likedVideos,
      [videoId]: !likedVideos[videoId]
    };
    setLikedVideos(newLikedVideos);
    localStorage.setItem('likedVideos', JSON.stringify(newLikedVideos));
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await fetch('/comments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
        },
        body: JSON.stringify({
          comment: {
            content: newComment,
            video_id: videoContent[currentVideoIndex].video_id
          }
        })
      });

      if (response.ok) {
        const comment = await response.json();
        setComments(prevComments => ({
          ...prevComments,
          [videoContent[currentVideoIndex].video_id]: [
            ...(prevComments[videoContent[currentVideoIndex].video_id] || []),
            comment
          ]
        }));
        setNewComment('');
      }
    } catch (error) {
      console.error('Error posting comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlers = useSwipeable({
    onSwipedUp: () => {
      if (currentVideoIndex < videoContent.length - 1) {
        setCurrentVideoIndex(prev => prev + 1);
      }
    },
    onSwipedDown: () => {
      if (currentVideoIndex > 0) {
        setCurrentVideoIndex(prev => prev - 1);
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  const commentsHandlers = useSwipeable({
    onSwiping: (e) => {
      if (e.deltaY > 0) {
        setIsDraggingComments(true);
        setCommentsPanelY(Math.min(e.deltaY, 300));
      }
    },
    onSwiped: (e) => {
      setIsDraggingComments(false);
      if (e.deltaY > 150) {
        setShowComments(false);
        setCommentsPanelY(0);
      } else {
        setCommentsPanelY(0);
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  const handleConsentAccept = () => {
    setAudioEnabled(true);
    setShowConsentDialog(false);
    setVideoPlaybackAllowed(true);
  };

  const handleKeepMuted = () => {
    setShowConsentDialog(false);
    setVideoPlaybackAllowed(true);
  };

  return (
    <div style={{ display: 'flex', height: '100vh', width: '100%' }}>
      {showConsentDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '400px',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: '16px' }}>Welcome to my "NickTok"</h2>
            <p style={{ marginBottom: '20px' }}>
              How would you like to watch the videos?
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={handleConsentAccept}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Play with Sound
              </button>
              <button
                onClick={handleKeepMuted}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Play Muted
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <div
        {...handlers}
        style={{
          width: isSidebarExpanded ? '360px' : '60px',
          backgroundColor: 'white',
          transition: 'all 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          padding: '20px 0',
          zIndex: 10,
          position: isSmallScreen ? 'absolute' : 'relative',
          height: '100%',
          boxShadow: isSmallScreen && isSidebarExpanded ? '0 0 15px rgba(0,0,0,0.1)' : 'none'
        }}
      >
        <div style={{
          padding: isSidebarExpanded ? '0 20px' : '0 10px',
          marginLeft: isSidebarExpanded ? '0' : 'auto',
          marginRight: isSidebarExpanded ? '0' : 'auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: isSidebarExpanded ? '20px' : '0'
        }}>
          {isSidebarExpanded && (
            <img
              src="/nick.png"
              alt="Nick Dowse"
              style={{
                width: '40%',
                height: 'auto',
                borderRadius: '4px'
              }}
            />
          )}
          <button
            onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
            style={{
              background: 'none',
              border: 'none',
              color: '#333',
              padding: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: isSidebarExpanded ? '0' : '0',
              marginRight: isSidebarExpanded ? '0' : '0'
            }}
          >
            <HiOutlineMenu size={24} />
          </button>
        </div>

        {isSidebarExpanded && (
          <>
            <div style={{ padding: '0 20px', marginBottom: '20px' }}>
              <h1 style={{
                fontSize: '24px',
                fontWeight: 'bold',
                marginBottom: '12px',
                color: '#333'
              }}>
                Hi, I'm Nick Dowse.
              </h1>
              <p style={{
                fontSize: '14px',
                lineHeight: '1.5',
                color: 'rgba(32, 32, 32, 1)'
              }}>
                Thanks for dropping by. I like to make things, currently working at Stripe, previously WeTransfer. In my spare time I like to answer questions I have about the world, and hopefully vibecode some of those answers.
              </p>
              <br></br>
              <p style={{
                fontSize: '14px',
                lineHeight: '1.5',
                color: 'rgba(32, 32, 32, 1)',
              }}>
                Have a look through some demos of some projects I've worked on recently and less recently. Feel free to leave a comment.
              </p>
              <br></br>
              <p  style={{
                fontSize: '14px',
                lineHeight: '1.5',
                color: 'rgba(32, 32, 32, 1)',
                marginBottom: '5px'
              }}>
                If you're brave enough you can view my old and more extensive personal website below:
              </p>
              <p>
                <a
                  href="https://nickdowse.me"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'rgb(67, 80, 178)'
                  }}
                >
                  <u>
                    nickdowse.me
                  </u>
                </a>

              </p>
            </div>

            <div style={{
              padding: '0 20px',
              marginTop: '0px',
              marginBottom: '20px'
            }}>
              <hr style={{
                border: 'none',
                borderTop: '1px solid #e0e0e0',
              }} />
            </div>

            {/* Video Grid */}
            <div style={{
              padding: '0 20px',
              marginBottom: '20px'
            }}>
              <h4>Projects</h4>
            </div>
            <div style={{
              padding: '0 20px',
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px'
            }}>

              {videoContent.map((content, index) => {
                const isComingSoon = content.display == false
                return (
                  <div
                    key={content.video_id}
                    onClick={() => !isComingSoon && setCurrentVideoIndex(index)}
                    style={{
                      cursor: isComingSoon ? 'default' : 'pointer',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      position: 'relative',
                      aspectRatio: '16/9',
                      backgroundColor: '#f0f0f0',
                      border: currentVideoIndex === index ? '2px solid #007bff' : '1px solid #e0e0e0',
                      transition: 'transform 0.2s ease',
                      ':hover': {
                        transform: isComingSoon ? 'none' : 'scale(1.02)'
                      }
                    }}
                  >
                    {!isComingSoon ? (
                      <video
                        src={content.src}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        poster={content.poster}
                        preload="metadata"
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#e0e0e0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <p>
                          <span style={{
                            color: '#999',
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }}>
                            Coming Soon
                          </span>
                        </p>
                      </div>
                    )}
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      padding: '8px',
                      background: 'rgba(0,0,0,0.7)',
                      color: isComingSoon ? '#999' : 'white',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      <p>
                        {content.title.replace(' [Coming Soon]', '')}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Backdrop for small screens */}
      {isSmallScreen && isSidebarExpanded && (
        <div
          onClick={() => setIsSidebarExpanded(false)}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9
          }}
        />
      )}

      {/* Main content */}
      <div
        {...handlers}
        style={{
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: '#f8f9fa',
          marginLeft: isSmallScreen ? 0 : (isSidebarExpanded ? 0 : '60px')
        }}
      >
        {videoContent.map((content, index) => {
          const isComingSoon = content.display == false
          return ( !isComingSoon &&
            <div
              className="video-container"
              key={content.video_id}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                transition: 'transform 0.3s ease-out',
                transform: `translateY(${(index - currentVideoIndex) * 100}vh)`
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}
                >
                  <video
                    ref={videoRefs[index]}
                    src={content.src}
                    loop={false}
                    playsInline
                    muted={!audioEnabled}
                    poster={content.poster}
                    onEnded={() => {
                      if (currentVideoIndex < videoContent.length - 1) {
                        setCurrentVideoIndex(prev => (prev + 1) % videoContent.length);
                      }
                    }}
                    onClick={() => {
                      if (videoRefs[index].current) {
                        if (videoRefs[index].current.paused) {
                          videoRefs[index].current.play();
                          setIsPlaying(true);
                        } else {
                          videoRefs[index].current.pause();
                          setIsPlaying(false);
                        }
                      }
                    }}
                    onDoubleClick={() => handleLikeVideo(content.video_id)}
                    style={{
                      width: 'auto',
                      height: 'auto',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      position: 'relative',
                      cursor: 'pointer'
                    }}
                  />

                  <div
                    style={{
                      position: 'absolute',
                      bottom: '0px',
                      left: isSmallScreen && !isSidebarExpanded ? '61px' : '1px',
                      color: 'white',
                      width: isSmallScreen ? (isSidebarExpanded ? 'calc(100% - 2px)' : 'calc(100% - 62px)') : (isSidebarExpanded ? 'calc(100% - 2px)' : 'calc(100% - 62px)'),
                      textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
                      padding: '10px',
                      background: 'rgba(0,0,0,0.5)',
                      borderRadius: '8px 8px 0px 0px',
                      zIndex: 2
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        right: '20px',
                        bottom: '100%',
                        marginBottom: '20px',
                        zIndex: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                        alignItems: 'center',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
                        padding: '10px',
                        borderRadius: '16px'
                      }}
                    >
                      <div
                        onClick={() => handleLikeVideo(content.video_id)}
                        style={{
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center'
                        }}
                      >
                        {likedVideos[content.video_id] ? (
                          <AiFillHeart size={28} color="#ff2d55" style={{ filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.7))' }} />
                        ) : (
                          <AiOutlineHeart size={28} color="white" style={{ filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.7))' }} />
                        )}
                        <p>
                          <span style={{
                            color: 'white',
                            fontSize: '14px',
                            marginTop: '4px',
                            fontWeight: 'bold'
                          }}>
                            {likedVideos[content.video_id] ? 'Liked' : 'Like'}
                          </span>
                        </p>
                      </div>

                      <div
                        onClick={() => setShowComments(!showComments)}
                        style={{
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center'
                        }}
                      >
                        <AiOutlineComment size={28} color="white" style={{ filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.7))' }} />
                        <p>
                          <span style={{
                            color: 'white',
                            fontSize: '14px',
                            marginTop: '4px',
                            fontWeight: 'bold'
                          }}>
                            Comments
                          </span>
                        </p>
                      </div>

                      <div
                        onClick={() => setAudioEnabled(!audioEnabled)}
                        style={{
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center'
                        }}
                      >
                        {audioEnabled ? (
                          <BsSoundwave size={28} color="white" style={{ filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.7))' }} />
                        ) : (
                          <BsVolumeMute size={28} color="white" style={{ filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.7))' }} />
                        )}
                        <p>
                          <span style={{
                            color: 'white',
                            fontSize: '14px',
                            marginTop: '4px',
                            fontWeight: 'bold'
                          }}>
                            {audioEnabled ? 'Sound on' : 'Sound off'}
                          </span>
                        </p>
                      </div>
                    </div>
                    <h2 style={{
                      margin: '0 0 8px 0',
                      fontSize: '1.5rem',
                      fontWeight: 'bold'
                    }}>
                      {content.title}
                    </h2>
                    <TextDescription text={content.description} />
                  </div>

                  {showComments && (
                    <div
                      {...commentsHandlers}
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '60%',
                        backgroundColor: 'rgba(0, 0, 0, 0.95)',
                        zIndex: 4,
                        borderTopLeftRadius: '20px',
                        borderTopRightRadius: '20px',
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        transform: `translateY(${isDraggingComments ? commentsPanelY : 0}px)`,
                        transition: isDraggingComments ? 'none' : 'transform 300ms ease-out',
                        boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.2), 0 -2px 4px -1px rgba(0, 0, 0, 0.2)'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px'
                      }}>
                        <h3 style={{ color: 'white', margin: 0 }}>Comments</h3>
                        <IoCloseOutline
                          size={24}
                          color="white"
                          style={{ cursor: 'pointer' }}
                          onClick={() => setShowComments(false)}
                        />
                      </div>

                      <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        marginBottom: '16px'
                      }}>
                        {(comments[content.video_id] || []).map((comment, i) => (
                          <div
                            key={i}
                            style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              padding: '12px',
                              borderRadius: '8px',
                              marginBottom: '8px'
                            }}
                          >
                            <div style={{
                              color: '#fff',
                              fontSize: '14px',
                              marginBottom: '4px',
                              fontWeight: 'bold'
                            }}>
                              <p>
                                {comment.user?.name || 'Anonymous'}
                              </p>
                            </div>
                            <div style={{ color: '#fff', fontSize: '14px' }}>
                              <p>
                                {comment.content}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      <form onSubmit={handleSubmitComment}>
                        <div style={{
                          display: 'flex',
                          gap: '8px'
                        }}>
                          <input
                            type="text"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Add a comment..."
                            style={{
                              flex: 1,
                              padding: '12px',
                              borderRadius: '20px',
                              border: 'none',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              color: 'white',
                              fontSize: '14px'
                            }}
                          />
                          <button
                            type="submit"
                            disabled={isSubmitting || !newComment.trim()}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '20px',
                              border: 'none',
                              backgroundColor: '#ff2d55',
                              color: 'white',
                              cursor: isSubmitting || !newComment.trim() ? 'not-allowed' : 'pointer',
                              opacity: isSubmitting || !newComment.trim() ? 0.5 : 1
                            }}
                          >
                            {isSubmitting ? 'Posting...' : 'Post'}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
              </div>
            </div>
          </div>
        )})}
      </div>
    </div>
  );
};

export default TikTok;
